import { REGEX_VALID_DOMAIN } from '../constants.ts'

/**
 * The rules behind `/register`, the page that finishes a node registration in the browser
 * (nobgp-router#1603, the app half).
 *
 * The agent prints `https://app.nobgp.com/register?session=<uuid>` and draws it as a QR code, so
 * the reader is often a phone and the machine is somewhere else. The page shows what the AGENT
 * announced about that machine, takes a network and a name, and then waits for the machine to
 * finish.
 *
 * ⚠⚠ THE ROUTER HALF IS NOT RELEASED YET. Every function here decodes defensively and every
 * failure has a sentence, because this page must say what is wrong rather than spin. The router
 * contract is on router#1603:
 *
 *   GET  <router>/v1/register/session/<uuid>   user bearer, answers the facts below
 *   POST <router>/v1/register/session/<uuid>   user bearer, body {network?, node_name?}, one-shot
 *
 * ⚠⚠ TWO THINGS THAT ARE NOT VISIBLE FROM THIS SIDE, and both of them bite. The POST answers
 * `204` WITH NO BODY, so the approval it accepted is a fact only this page holds until the next
 * read. And every refusal is `http.Error`, so it is `text/plain` — an expired record, an unknown
 * network and a router that has no such route all arrive the same way, and they are told apart by
 * what the answer SAYS.
 *
 * ⚠ `network` goes in the body only when the GET said `choice_required`. The router resolves the
 * single network itself, and it refuses an empty `network` when a choice IS required rather than
 * picking one for the reader.
 *
 * The rules live here, and not in the component, because this repo has no test runner: a pure
 * function is extracted and asserted against. See registerSession.test.ts beside this file.
 */

/**
 * How long the router keeps the record. The page cannot outlive it.
 *
 * ⚠ The 15 minutes run from the moment the MACHINE announced itself, and no read or write extends
 * them. So a reader who leaves the page open can watch it expire, and the expired card is a state
 * this page really reaches — not a corner.
 */
export const REGISTER_SESSION_TTL_MS = 15 * 60 * 1000

/** How often the page reads the record. */
export const REGISTER_POLL_MS = 2500

/**
 * How long the page waits for the machine after the choice is sent, before it says so.
 *
 * ⚠ A page that waits forever is the browser version of agent#1204: the terminal menu that wedged
 * a fresh install with no sign of what it wanted. The machine can fail to finish for reasons this
 * page cannot see — the user closed the terminal, the install failed, the box lost the network —
 * so the wait is bounded and the reader gets a way out. The reader may extend it, because a slow
 * box is also a real cause.
 */
export const REGISTER_WAIT_LIMIT_MS = 120 * 1000

/**
 * How long the page waits for a registered node to come online before it opens the node page
 * anyway.
 *
 * ⚠⚠ THE ROUTER SAYS "REGISTERED" BEFORE THE NODE IS ONLINE (app#366, and again app#433). It
 * writes the node when the machine asks for its token, and the node is online only when the agent
 * connects, a few seconds later. A replaced node still has the `offline_at` of the machine it
 * replaced until then. So a node page opened at once reads the node offline, and shows it offline
 * until a realtime event arrives, which the tab may never get while this machine brings up its
 * TUN, routes and DNS.
 *
 * ⚠ Bounded, because a machine that registered and then failed to connect is real too: the node
 * page then says offline, which is the truth.
 */
export const REGISTER_CONNECT_WAIT_MS = 30 * 1000

/**
 * Whether the page may open the node page: the node is online, or the page waited long enough.
 * `online` is null when the node could not be read, which is not a reason to open it early.
 */
export const opensNode = (
  online: boolean | null,
  waitedMs: number,
  limitMs: number = REGISTER_CONNECT_WAIT_MS
): boolean => online === true || waitedMs >= limitMs

/** One network the user may put the machine in. The router answers names; an id is optional. */
export interface RegisterNetwork {
  name: string
  id?: string
}

/** An org and the networks the user may put the machine in there. */
export interface RegisterOrg {
  id: string
  name: string
  networks: { id: string; name: string }[]
}

/**
 * What the record holds. Every field is optional, because the record fills up in stages.
 *
 * ⚠ The page is an APPROVAL and not only a picker (Benoit, 2026-09-17). The reader looks at what
 * the machine said about itself and says yes; the POST is that yes, and it is what releases the
 * machine to register. The network is a QUESTION only when the account has more than one, and the
 * router answers that question with `choice_required` rather than leaving the page to count.
 */
export interface RegisterSession {
  /** Announced by the agent. */
  hostname: string | null
  platform: string | null
  agentVersion: string | null
  /**
   * The short code the machine's terminal prints (`XXXX-XXXX`, router#1752). The page shows it
   * above the approve button, so the reader can see that the machine they approve is the one in
   * front of them. Null when the router sends none: a router or an agent that predates router#1752.
   */
  userCode: string | null
  /** Observed by the router on the announce, then resolved to a place. Both are hints. */
  ip: string | null
  city: string | null
  country: string | null
  /**
   * The user's networks, flat, by name — the list an older router sends — and whether the reader
   * has to pick one. `orgs` below supersedes both where the router sends it.
   */
  networks: RegisterNetwork[]
  choiceRequired: boolean
  /**
   * The user's orgs, each with its networks, by id. Empty on a router that predates it; the page
   * then falls back to `networks` and approves by NAME.
   *
   * ⚠⚠ A network name is unique per ORG, not globally. Two orgs may each hold `home`, so where the
   * router gives ids the page picks and approves by id and never sends a name.
   */
  orgs: RegisterOrg[]
  /** The name the router proposes — the machine's own hostname. The reader may change it. */
  suggestedName: string | null
  /** The approval, once this page has sent it. `network` is absent when the router resolved it. */
  network: string | null
  nodeName: string | null
  /** Written by `POST /v1/register` when the machine finishes. */
  nodeId: string | null
  networkId: string | null
  /** `waiting` → `registering` → `registered`. Empty on a router that does not send it. */
  status: RegisterStatus | null
}

/** The stage, in the router's words. */
export type RegisterStatus = 'waiting' | 'registering' | 'registered' | (string & {})

/**
 * Why the page cannot go on.
 *
 * `unavailable` is the one that exists only until the router ships. A router without the route
 * answers Go's own `404 page not found`, so the page can tell "this router does not serve
 * registration pages" from "your link has expired" and say the right thing to the user. Both are
 * 404.
 *
 * ⚠⚠ THE TEST IS THAT SENTENCE, NOT THE CONTENT TYPE. The router refuses a gone record with
 * `http.Error` (router#1604 `writeSessionError`), which is `text/plain` as well — so "the answer
 * was not JSON" reads EVERY expired link as a router that cannot do this yet, and sends a reader
 * whose link simply ran out back to a machine that is waiting for them. Go's route miss is the
 * one body that is fixed and knowable, so it is the one that is matched.
 */
export type RegisterFailureKind =
  | 'expired'
  | 'forbidden'
  | 'unavailable'
  | 'unauthenticated'
  | 'already'
  | 'name_taken'
  | 'cancelled'
  | 'attach_refused'
  | 'error'

export interface RegisterFailure {
  kind: RegisterFailureKind
  message: string
  /** True when reading again may succeed. The page keeps polling on these. */
  retryable: boolean
  /**
   * Only on `name_taken`: what the router found when it checked the name again at approval, and
   * the free name it proposes. The caller knows which name it sent, so the name is not here.
   */
  conflict?: { status: NameConflictStatus; suggested: string | null }
}

/**
 * The machine stopped waiting (Benoit, 2026-09-17; router#1609): a registration key was pasted
 * into its terminal, the reader pressed Ctrl-C there, or its own wait ran out. It tells the
 * router, and the record says `cancelled`. Nothing on this page can bring it back, so the page
 * says so and gives a way out — and never shows the approve form for it.
 */
export const CANCELLED_MESSAGE =
  'This machine is no longer waiting. It was registered another way, or registration was stopped on the machine.'

const EXPIRED_MESSAGE =
  'This registration has expired. A link is good for 15 minutes from the moment the machine asks. Run the installer again on the machine to get a new one.'

/**
 * Turns a failed read into a sentence.
 *
 * `body` is the parsed JSON, or null when the answer was not JSON; `text` is the raw answer, which
 * is where a router that answers with `http.Error` puts its words. `status` is 0 for a request
 * that never got an answer.
 */
export const classifyFailure = (status: number, body: unknown = null, text: string = ''): RegisterFailure => {
  const message = failureMessage(body) || plainMessage(text)

  if (status === 0)
    return {
      kind: 'error',
      message: 'Cannot reach the router. Check your connection.',
      retryable: true,
    }
  if (status === 401)
    return { kind: 'unauthenticated', message: 'Sign in to finish this registration.', retryable: false }
  // ⚠ OUR SENTENCE, not the router's, on the two refusals a reader can act on. The router says
  // "not found" and "forbidden" to a machine; a person needs the remedy — run the installer again,
  // or sign in as the account that started this. The router's words go to the console instead.
  // ⚠⚠ NOT EVERY 403 IS "ANOTHER ACCOUNT" (app#397). The approval also asks the attach rule
  // (PERMISSIONS.md § 5 Attachment, router#1761 `RequireAttach`): a network whose "members may add
  // nodes" is off refuses a member who is not its owner, with a 403 in text/plain that names the
  // setting. That refusal is about the NETWORK, not the record, so it is not terminal: the page says
  // it beside the button and the reader chooses another network.
  if (status === 403 && ATTACH_REFUSED.test(text))
    return {
      kind: 'attach_refused',
      message: MEMBERS_MAY_NOT_ADD.test(text) ? ATTACH_REFUSED_MESSAGE : ATTACH_REFUSED_OTHER_MESSAGE,
      retryable: false,
    }
  if (status === 403)
    return {
      kind: 'forbidden',
      message:
        'This registration belongs to another account. Sign in with the account that started it, or run the installer again on the machine.',
      retryable: false,
    }
  // ⚠ The registration is already answered, which is not an error the reader made: the router
  // takes one choice and refuses a second (router#1604 `errAlreadyChosen`). The page waits for the
  // machine instead of showing a refusal for something that is going well.
  if (status === 409)
    return {
      kind: 'already',
      message: 'This registration has already been approved. Waiting for the machine to finish.',
      retryable: false,
    }
  // ⚠ The reader approved AFTER the machine cancelled. The same final state as a read that says
  // `cancelled` — not an error, because the reader did nothing wrong. Matched on the router's JSON
  // `{"error":"cancelled"}` only: a 410 that does not say so stays the expired record it was.
  if (status === 410 && isCancelledBody(body))
    return { kind: 'cancelled', message: CANCELLED_MESSAGE, retryable: false }
  if (status === 404 || status === 410) {
    // ⚠ Only Go's own route miss means the router has no such route. Everything else with this
    // status is a record that is gone — and the router says THAT in text/plain too.
    if (isRouteMissing(body, text))
      return {
        kind: 'unavailable',
        message:
          'This router cannot finish a registration in the browser yet. Go back to the machine and finish the installation there.',
        retryable: false,
      }
    return { kind: 'expired', message: EXPIRED_MESSAGE, retryable: false }
  }
  // ⚠ The approval checks the name AGAIN (Benoit, router#1603 "a name that is already taken"),
  // because the node that holds it can come online or go offline after the page last asked. The
  // router answers 422 with the same facts the name check gives, and the page shows the same
  // notice and lets the reader approve again. Not terminal: nothing about the record is wrong.
  if (status === 422) {
    const conflict = nameConflict(body)
    if (conflict)
      return {
        kind: 'name_taken',
        message:
          conflict.status === 'online'
            ? 'That name is now in use by an online node. Check the name and approve again.'
            : conflict.status === 'unavailable'
              ? 'That name cannot be used. Check the name and approve again.'
              : 'That name now belongs to an offline node. Choose what to do and approve again.',
        retryable: false,
        conflict,
      }
  }
  if (status >= 500)
    return { kind: 'error', message: 'The router had a problem. Trying again.', retryable: true }

  return { kind: 'error', message: message || `The router refused the request (${status}).`, retryable: false }
}

/**
 * The router's attach refusals (`RequireAttach`, router#1761), matched on their text: the router
 * writes them with `http.Error`, so there is no code to match. The first form is the setting; the
 * second is a reader who is not a member of the network's org.
 */
const MEMBERS_MAY_NOT_ADD = /does not let members add nodes|members_may_add_nodes/i
const ATTACH_REFUSED = /does not let members add nodes|members_may_add_nodes|adding a node to network/i

export const ATTACH_REFUSED_MESSAGE =
  'You cannot add a node to that network: only its owner, or an owner or admin of the organization, can. Choose another network, or create one. To replace a node of yours that is offline, use its name.'
export const ATTACH_REFUSED_OTHER_MESSAGE =
  'You cannot add a node to that network. Choose another network, or create one.'

/**
 * A router that has no such route, told apart from one whose record is gone.
 *
 * Go's `http.NotFound` writes exactly `404 page not found`, and an app served the same 404 by a
 * CDN sends HTML or nothing at all. Neither is the router refusing a registration.
 */
const ROUTE_MISSING = /^404 page not found\b/i

const isRouteMissing = (body: unknown, text: string): boolean => {
  if (body !== null && body !== undefined) return false
  const answer = text.trim()
  return answer === '' || ROUTE_MISSING.test(answer) || answer.startsWith('<')
}

/**
 * The words a `text/plain` refusal carried, when they can be shown.
 *
 * ⚠ An error page from a proxy is not a sentence for a person, so only a short answer with no
 * markup in it is used. The two refusals this matters for are the router's own: "network is
 * required: this account has more than one" and "unknown network".
 */
const plainMessage = (text: string): string => {
  const answer = text.trim()
  if (!answer || answer.length > 200 || answer.includes('<') || ROUTE_MISSING.test(answer)) return ''
  return answer.charAt(0).toUpperCase() + answer.slice(1)
}

const isCancelledBody = (body: unknown): boolean =>
  !!body && typeof body === 'object' && (body as Record<string, unknown>).error === 'cancelled'

/** The router's own words for a refusal, when it sent any. */
const failureMessage = (body: unknown): string => {
  if (!body || typeof body !== 'object') return ''
  const record = body as Record<string, unknown>
  const message = record.message ?? record.error
  return typeof message === 'string' ? message : ''
}

const text = (value: unknown): string | null => {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed ? trimmed : null
}

/**
 * The network list, from either shape.
 *
 * `/v1/register` answers an array of NAMES today (router `handleOAuthRegister`), and the contract
 * says the new endpoint answers "the same list". An array of objects is accepted as well, so that
 * a router that grows an id for each network does not break this page. Blanks and duplicates are
 * dropped, and the order the router sent is kept.
 */
export const parseNetworks = (value: unknown): RegisterNetwork[] => {
  if (!Array.isArray(value)) return []
  const seen = new Set<string>()
  const networks: RegisterNetwork[] = []
  for (const entry of value) {
    let name: string | null = null
    let id: string | undefined
    if (typeof entry === 'string') {
      name = text(entry)
    } else if (entry && typeof entry === 'object') {
      const record = entry as Record<string, unknown>
      name = text(record.name)
      id = text(record.id) ?? undefined
    }
    if (!name || seen.has(name)) continue
    seen.add(name)
    networks.push(id ? { name, id } : { name })
  }
  return networks
}

/**
 * The org list, tolerantly. An org with no id is dropped, because nothing can be created in it and
 * nothing in it can be approved by id. A network with no id is dropped for the same reason. An org
 * with NO networks is kept: the reader may create the first one there.
 */
export const parseOrgs = (value: unknown): RegisterOrg[] => {
  if (!Array.isArray(value)) return []
  const orgs: RegisterOrg[] = []
  const seen = new Set<string>()
  for (const entry of value) {
    if (!entry || typeof entry !== 'object') continue
    const record = entry as Record<string, unknown>
    const id = text(record.id)
    if (!id || seen.has(id)) continue
    seen.add(id)
    const networks: { id: string; name: string }[] = []
    const networkIds = new Set<string>()
    for (const item of Array.isArray(record.networks) ? record.networks : []) {
      if (!item || typeof item !== 'object') continue
      const net = item as Record<string, unknown>
      const netId = text(net.id)
      const netName = text(net.name)
      if (!netId || !netName || networkIds.has(netId)) continue
      networkIds.add(netId)
      networks.push({ id: netId, name: netName })
    }
    orgs.push({ id, name: text(record.name) ?? 'Unnamed organization', networks })
  }
  return orgs
}

/** The record, from the router's JSON. Anything missing reads as null. */
export const parseRegisterSession = (body: unknown): RegisterSession => {
  const record = body && typeof body === 'object' ? (body as Record<string, unknown>) : {}
  const networks = parseNetworks(record.networks)
  return {
    hostname: text(record.hostname),
    platform: text(record.platform),
    agentVersion: text(record.agent_version),
    userCode: text(record.user_code),
    ip: text(record.ip),
    city: text(record.city),
    country: text(record.country),
    networks,
    // ⚠ Only an explicit `true` asks the reader to pick. A router that does not send the field, or
    // sends something else, must not turn a one-network account into a list of one.
    choiceRequired: record.choice_required === true,
    orgs: parseOrgs(record.orgs),
    suggestedName: text(record.suggested_name),
    network: text(record.network),
    nodeName: text(record.node_name),
    nodeId: text(record.node_id),
    networkId: text(record.network_id),
    status: text(record.status) ?? text(record.state),
  }
}

/**
 * The machine finished.
 *
 * ⚠ The node id alone, in a network and in none (nobgp-router#2137): the router says `registered`
 * exactly when `node_id` is set, and writes the network id in the same step. Never ask for a network
 * id: a node in no network has none.
 */
export const isComplete = (session: RegisterSession): boolean => !!session.nodeId

/**
 * The approval is already on the record, so the page waits instead of asking again. A reload after
 * the approval must not offer a second one, and the router refuses one anyway.
 */
export const isApproved = (session: RegisterSession): boolean =>
  session.status === 'registering' || session.status === 'registered' || !!session.network || !!session.nodeName

/**
 * Which network the machine joins when the reader does not pick.
 *
 * The router resolves the single network itself, so this is for the SENTENCE on the page — "it
 * joins home" — and never for the request. Null when the page cannot name it, and the sentence
 * then says only that the account has one network.
 */
export const plannedNetwork = (session: RegisterSession): string | null =>
  session.network ?? (session.networks.length === 1 ? session.networks[0].name : null)

/** Where the page goes when the machine finishes. */
export const nodePagePath = (nodeId: string | null): string | null => (nodeId ? `/nodes/${nodeId}` : null)

/**
 * The sign-in leg.
 *
 * ⚠⚠ THIS IS NOT A PLAIN SIGN-IN AND MUST NOT BE REPLACED BY ONE. `/auth/login?auth_session=…` is
 * a leg that deposits the agent's token against the session (router `provider.go`). Since
 * router#1609 the APPROVAL deposits the approving user's token too, so a reader who was already
 * signed in no longer strands the machine — but this leg is still the one that binds the session
 * to the reader's sign-in, and a visitor sent to the app's own `/login` would sign in without it,
 * and on an older router leave the machine waiting for a token that
 * never arrives. `returnTo` brings the reader back to this page with their own app session, which
 * the router's callback grants in the same hop.
 */
export const registerLoginUrl = (routerBase: string, appOrigin: string, sessionId: string): string => {
  const base = routerBase.replace(/\/$/, '')
  const origin = appOrigin.replace(/\/$/, '')
  const returnTo = `${origin}/register?session=${encodeURIComponent(sessionId)}`
  return `${base}/auth/login?auth_session=${encodeURIComponent(sessionId)}&returnTo=${encodeURIComponent(returnTo)}`
}

/**
 * A session id is a uuid. The page checks the shape before it asks the router, so a truncated
 * paste from a terminal reads as a bad link and not as an expired one.
 */
export const isSessionId = (value: string | null | undefined): boolean =>
  !!value && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value.trim())

/**
 * The name the page offers first.
 *
 * The agent chooses the hostname when nobody chooses for it, so the page starts from the same
 * value and lets the reader change it. The first label is taken, lowercased, and every character
 * the node-name rule refuses becomes a hyphen — `Benoit's-MBP.local` reads as `benoit-s-mbp`.
 * An empty answer is valid: the field then shows its placeholder and the router uses the hostname.
 */
export const defaultNodeName = (hostname: string | null): string => {
  if (!hostname) return ''
  const label = hostname.trim().split('.')[0].toLowerCase()
  const cleaned = label
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 63)
    .replace(/-+$/g, '')
  return cleaned
}

/** The same rule the rest of the app uses for a node name. A blank name is allowed. */
export const isValidNodeName = (name: string): boolean => !name || REGEX_VALID_DOMAIN.test(name)

/** What the page is doing. */
export type RegisterPhase = 'loading' | 'approve' | 'waiting' | 'overdue' | 'done' | 'cancelled'

/**
 * The phase, from the record and the clock.
 *
 * `waitedMs` is the time since the approval was sent, and `limitMs` is how long the page waits
 * before it says the machine has not finished. `overdue` is not an end: the page keeps polling,
 * because a slow machine still finishes and the record is good for 15 minutes.
 *
 * ⚠⚠ `approvedHere` is THIS page having sent the approval, and it is not the same fact as the
 * record carrying one. The router answers the approval `204` with no body, and a poll that was
 * already in flight answers with the record as it was BEFORE it — either one leaves the page
 * holding a record that says nobody has approved anything, and the reader is shown the approve
 * form again for a registration that is already under way. A second press then earns a refusal.
 */
export const registerPhase = (
  session: RegisterSession | null,
  waitedMs: number,
  limitMs: number = REGISTER_WAIT_LIMIT_MS,
  approvedHere: boolean = false
): RegisterPhase => {
  if (!session) return 'loading'
  if (isComplete(session)) return 'done'
  // ⚠ FINAL, and ahead of the approve form: a cancelled machine must never be offered an approval
  // it can no longer take. An older router never sends it, so nothing changes there.
  if (session.status === 'cancelled') return 'cancelled'
  if (!isApproved(session) && !approvedHere) return 'approve'
  return waitedMs >= limitMs ? 'overdue' : 'waiting'
}

/** The location line under the address. Empty when the lookup had no answer. */
export const locationLine = (city: string | null, country: string | null): string => {
  if (city && country) return `${city}, ${country}`
  return city || country || ''
}

/** "Debian 12 · agent 0.4.128", with the parts that are missing left out. */
export const machineLine = (platform: string | null, agentVersion: string | null): string =>
  [platform, agentVersion && `agent ${agentVersion}`].filter(Boolean).join(' · ')

/** "4 minutes" / "35 seconds", for the sentence that says how long the page has waited. */
export const waitedLabel = (waitedMs: number): string => {
  const seconds = Math.max(0, Math.round(waitedMs / 1000))
  if (seconds < 60) return `${seconds} second${seconds === 1 ? '' : 's'}`
  const minutes = Math.round(seconds / 60)
  return `${minutes} minute${minutes === 1 ? '' : 's'}`
}

// ── round 2 (Benoit's end-to-end test, 2026-09-17) ───────────────────────────

/**
 * A failure that ends the page. Everything else keeps the poll going.
 *
 * ⚠⚠ THE POLL STOPS ONLY WHEN THE RECORD CAN NEVER ANSWER AGAIN. The first version stopped on any
 * refusal it could not retry, and it gave up on a machine that came online. The record lives 15
 * minutes from the machine's announce, so a 404 is the one honest end. A refusal for another
 * account, a router with no such route, and a lost session end it too, because no later read can
 * change their answer. A stray 400 or a 500 does not, and neither does a `409`: that one says the
 * registration is already approved, and the page waits for the machine.
 */
export const isTerminal = (failure: RegisterFailure | null): boolean =>
  !!failure &&
  (failure.kind === 'expired' ||
    failure.kind === 'cancelled' ||
    failure.kind === 'forbidden' ||
    failure.kind === 'unavailable' ||
    failure.kind === 'unauthenticated')

/** What the reader has picked. Ids where the router sends orgs; a name on an older router. */
export interface RegisterSelection {
  orgId: string
  networkId: string
  /** Only for the flat fallback: an older router sends names and no ids. */
  networkName: string
  nodeName: string
  /**
   * "Keep this node private" (router PERMISSIONS.md § 4.1, D-20, app#375): the new node starts
   * Private (you and org admins) instead of following its network. Absent or false: it follows.
   */
  keepPrivate?: boolean
}

/** True when the router sends orgs, and so the page picks and approves by id. */
export const byId = (session: RegisterSession): boolean => session.orgs.length > 0

/** Every network the reader could pick, across every org. */
export const networkCount = (session: RegisterSession): number =>
  byId(session) ? session.orgs.reduce((sum, org) => sum + org.networks.length, 0) : session.networks.length

/**
 * The org the page starts on. Preset when there is nothing to choose, and when the reader has no
 * network anywhere: then the first thing to do is create one, and the first org is where it goes
 * unless the reader moves it.
 */
export const defaultOrgId = (session: RegisterSession): string => {
  if (session.orgs.length === 1) return session.orgs[0].id
  if (session.orgs.length > 1 && networkCount(session) === 0) return session.orgs[0].id
  return ''
}

/**
 * The network the page starts on, inside an org. Preset only when that org has exactly one: with
 * more, the reader picks, because putting a machine in the wrong network is not undone by a click.
 */
export const defaultNetworkId = (session: RegisterSession, orgId: string): string => {
  const org = session.orgs.find(o => o.id === orgId)
  return org && org.networks.length === 1 ? org.networks[0].id : ''
}

/** The org selector shows only when there is more than one org. */
export const showsOrgPicker = (session: RegisterSession): boolean => session.orgs.length > 1

/** The network selector shows only when the chosen org has more than one network. */
export const showsNetworkPicker = (session: RegisterSession, orgId: string): boolean =>
  byId(session)
    ? (session.orgs.find(o => o.id === orgId)?.networks.length ?? 0) > 1
    : session.choiceRequired && session.networks.length > 1

/** The name of the picked network, for the sentence "Joins network …". */
export const selectedNetworkName = (session: RegisterSession, selection: RegisterSelection): string | null => {
  if (!byId(session)) return selection.networkName || plannedNetwork(session)
  const org = session.orgs.find(o => o.id === selection.orgId)
  return org?.networks.find(n => n.id === selection.networkId)?.name ?? null
}

/** The approval can be sent: a network is settled and the name is valid. */
export const canApprove = (session: RegisterSession, selection: RegisterSelection): boolean => {
  if (!isValidNodeName(selection.nodeName)) return false
  if (byId(session)) return !!selection.networkId
  if (session.networks.length === 0) return false
  return !session.choiceRequired || !!selection.networkName
}

/**
 * The POST body.
 *
 * ⚠⚠ `network_id` WHERE THE PAGE HAS ONE, AND THEN NEVER A NAME. A name is ambiguous across orgs.
 * On an older router, with no ids, the name goes only when the router asked for a choice, as it
 * did before; the router resolves a single network itself. An empty node name is left out, and
 * the router then keeps the name it suggested.
 */
export const approvalBody = (
  session: RegisterSession,
  selection: RegisterSelection,
  replace: boolean = false
): Record<string, string | boolean> => {
  const body: Record<string, string | boolean> = {}
  if (byId(session)) {
    if (selection.networkId) body.network_id = selection.networkId
  } else if (session.choiceRequired && selection.networkName) {
    body.network = selection.networkName
  }
  if (selection.nodeName) body.node_name = selection.nodeName
  // ⚠⚠ `replace` ONLY WHEN THE READER CHOSE IT, and never `false` spelled out. Replacing hands this
  // machine another node's id, storage and grants — right for a reinstall of the same box, wrong
  // for anything else — so it is never a default. Leaving `false` out also keeps the body the
  // shape an older router already takes.
  if (replace) body.replace = true
  // ⚠ The starting setting rides only for a NEW node, and only when it is Private. The router
  // writes it once, in the INSERT of a node the registration CREATES; a replace keeps the replaced
  // node's own setting, so sending one there would promise a change that does not happen. Follow is
  // what the router does with no field, and leaving it out keeps the body an older router takes.
  if (!replace && selection.keepPrivate) body.access = 'private'
  return body
}

/** What the unauthenticated preview told the page before sign-in. */
export type RegisterPreview =
  | { kind: 'facts'; hostname: string | null; platform: string | null }
  | { kind: 'generic' }
  | { kind: 'expired' }

/**
 * The preview answer, from `GET /v1/register/session/<uuid>/preview`.
 *
 * ⚠ The preview binds nothing. Only the authenticated GET binds the session to a user, so reading
 * this is safe before sign-in and says nothing about who will approve.
 *
 * A 404 is an expired or unknown session, and the page says so BEFORE sending the reader through
 * a sign-in that leads nowhere — EXCEPT Go's own `404 page not found`, which is a router that
 * predates the preview. The router refuses a gone record with `http.Error`, which is `text/plain`
 * too, so the content type cannot tell the two apart; the sentence can (`isRouteMissing`, as for
 * the authenticated read). The page then shows the generic sentence and goes on. Any other failure
 * also falls back to the generic sentence: the preview is a courtesy, and it must never stop a
 * sign-in.
 */
export const classifyPreview = (status: number, body: unknown, text: string = ''): RegisterPreview => {
  if (status >= 200 && status < 300) {
    const record = body && typeof body === 'object' ? (body as Record<string, unknown>) : {}
    return { kind: 'facts', hostname: textField(record.hostname), platform: textField(record.platform) }
  }
  if ((status === 404 || status === 410) && !isRouteMissing(body, text)) return { kind: 'expired' }
  return { kind: 'generic' }
}

/** The landing sentence. */
export const landingTitle = (hostname: string | null): string =>
  hostname ? `Register ${hostname} as a new noBGP node` : 'Registering a new noBGP node'

/** The expired sentence, shared by the preview and the authenticated read. */
export const expiredMessage = (): string => EXPIRED_MESSAGE

const textField = (value: unknown): string | null => text(value)

// ── round 3: a name that is already taken (Benoit, router#1603, 2026-09-17) ──

/**
 * What the router says about a name in a network: `GET …/name?network_id=&name=`.
 *
 * - `free`: nobody holds it. No notice.
 * - `online`: an online node holds it. The page says so and PRE-FILLS the router's free name.
 *   The field stays editable; nothing is blocked and nothing is renamed without the reader seeing.
 * - `offline`: an offline node holds it. The reader must CHOOSE: replace that node (a reinstall of
 *   the same box — this machine takes its id, storage and grants), or register as the suggested
 *   name (a different machine). Neither is picked for them.
 * - `unavailable`: the name cannot be used at all — the node that holds it is revoked, or it is
 *   in a contested rename. Like `online`: say so and pre-fill the free name. There is NO replace
 *   option: nothing the reader picks here can make that name usable.
 */
export type NameStatus = 'free' | 'online' | 'offline' | 'unavailable'

/** A name that is not free. */
export type NameConflictStatus = Exclude<NameStatus, 'free'>

export interface NameCheck {
  name: string
  networkId: string
  status: NameStatus
  suggested: string | null
}

/** The check, or why there is none. */
export type NameCheckResult =
  | { kind: 'checked'; check: NameCheck }
  | { kind: 'none' }

const CONFLICT_ERRORS: Record<string, NameConflictStatus> = {
  name_online: 'online',
  name_offline: 'offline',
  name_unavailable: 'unavailable',
}

const nameConflict = (body: unknown): { status: NameConflictStatus; suggested: string | null } | null => {
  if (!body || typeof body !== 'object') return null
  const record = body as Record<string, unknown>
  const status = typeof record.error === 'string' ? (CONFLICT_ERRORS[record.error] ?? null) : null
  if (!status) return null
  return { status, suggested: textField(record.suggested) }
}

/**
 * The name check's answer.
 *
 * ⚠⚠ ONLY A 200 JSON VERDICT MAKES A NOTICE. Every other answer is `none` — no notice, approve as
 * today. That is the rule, and not a list of cases, because the router that is live today does
 * not answer 404 here: `<uuid>/name` does not parse as a session id there, so it answers `400
 * session must be a UUID` in text/plain. The same rule covers a 400 for a name the field already
 * refuses, a 5xx and a blip. An expired record or another account's session also read as `none` here:
 * the poll owns those, and it says them on its own card. The approval re-checks the name anyway,
 * so a check that never answered cannot let a taken name through.
 */
export const classifyNameCheck = (
  status: number,
  body: unknown,
  asked: { name: string; networkId: string }
): NameCheckResult => {
  if (status < 200 || status >= 300 || !body || typeof body !== 'object') return { kind: 'none' }
  const record = body as Record<string, unknown>
  const answer = record.status
  if (answer !== 'free' && answer !== 'online' && answer !== 'offline' && answer !== 'unavailable')
    return { kind: 'none' }
  return {
    kind: 'checked',
    check: {
      // The name ASKED, not the one echoed: that is the key the page compares against, and a router
      // that normalised the name must not make a current answer look stale.
      name: asked.name,
      networkId: asked.networkId,
      status: answer,
      suggested: answer === 'free' ? null : textField(record.suggested),
    },
  }
}

/**
 * The answer belongs to what the reader has NOW. The check is debounced and can be slow; a reader
 * who typed on in the meantime must not get a notice about a name they left behind.
 */
export const isCurrentCheck = (check: NameCheck | null, name: string, networkId: string): check is NameCheck =>
  !!check && check.name === name && check.networkId === networkId

/**
 * The name the router will use: what the reader typed, or — when the field is blank — the name it
 * proposed, cleaned the way the page cleans it. This is the name to check.
 */
export const effectiveNodeName = (session: RegisterSession, typed: string): string =>
  typed || defaultNodeName(session.suggestedName ?? session.hostname)

/** The reader's answer to an offline name. Empty until they pick. */
export type OfflineChoice = '' | 'replace' | 'new'

/** A name conflict the page has seen, kept after it pre-fills, so the reader can see why. */
export interface NameNotice {
  name: string
  networkId: string
  status: NameConflictStatus
  suggested: string | null
}

/** What to show under the name field. */
export type NameNoticeView =
  | { kind: 'online' | 'unavailable'; name: string; suggested: string | null }
  | { kind: 'offline'; name: string; suggested: string | null; choice: OfflineChoice }

/**
 * The notice to show for the name the reader has now.
 *
 * The notice outlives the name it is about, on purpose: after the page pre-fills `ubuntu-1`, the
 * reader still sees that `ubuntu` was taken and why the field changed. It goes when the reader
 * types something else, or moves to another network, where the conflict does not exist.
 */
export const nameNoticeView = (
  notice: NameNotice | null,
  name: string,
  networkId: string,
  choice: OfflineChoice
): NameNoticeView | null => {
  if (!notice || notice.networkId !== networkId) return null
  const onName = name === notice.name
  const onSuggested = !!notice.suggested && name === notice.suggested
  if (notice.status !== 'offline')
    return onName || onSuggested ? { kind: notice.status, name: notice.name, suggested: notice.suggested } : null
  // Offline: the choice drives the name — "replace" keeps it, "new" moves to the suggestion.
  if (onName || (onSuggested && choice === 'new'))
    return { kind: 'offline', name: notice.name, suggested: notice.suggested, choice: onName && choice === 'new' ? '' : choice }
  return null
}

/** An offline name with no choice made blocks the approval. Nothing else about a name does. */
export const nameBlocksApproval = (view: NameNoticeView | null): boolean => view?.kind === 'offline' && !view.choice

/** The approval carries `replace: true` only for an offline name the reader chose to replace. */
export const replaceChosen = (view: NameNoticeView | null): boolean => view?.kind === 'offline' && view.choice === 'replace'
